import { Check, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react'

import { clamp, cn } from '~/lib/utils'

import { IconButton } from './IconButton'
import { inputWrapper } from './Input'
import { Menu, MenuIcon, MenuItem } from './Menu'
import { $currentProjectRecordsLimit } from '~/features/projects/stores/current-project.ts'

const LIMIT_OPTIONS = [10, 25, 50, 100, 250, 500, 1000]

export type PaginatorProps = TInheritableElementProps<
  'div',
  {
    limit: number
    onFirst: (event?: unknown) => void
    onLast: (event?: unknown) => void
    onNext: (event?: unknown) => void
    onPrev: (event?: unknown) => void
    skip: number
    total: number
  }
>

export function Paginator({
  total,
  skip,
  limit,
  onFirst,
  onLast,
  onNext,
  onPrev,
  className,
  ...props
}: PaginatorProps) {
  const onFirstPage = skip <= 0
  const onLastPage = skip >= total - limit
  return (
    <div className={cn('flex items-center gap-4 py-2 pr-5 pl-1.5', className)} {...props}>
      <div className="flex items-center gap-x-2">
        <Menu
          align="start"
          className="min-w-24"
          trigger={
            <button
              aria-label="Records per page"
              title="Records per page"
              className={cn(inputWrapper({ size: 'small' }), 'mr-2 w-20 cursor-pointer justify-between')}
            >
              {limit}
              <MenuIcon size={16} />
            </button>
          }
        >
          {LIMIT_OPTIONS.map((option) => (
            <MenuItem
              aria-selected={option === limit}
              className="h-9 flex-row px-3"
              key={option}
              onSelect={() => $currentProjectRecordsLimit.set(option)}
            >
              {option}
              <Check className={cn(option !== limit && 'invisible')} />
            </MenuItem>
          ))}
        </Menu>
        <IconButton
          aria-label="First page"
          disabled={onFirstPage}
          onClick={onFirst}
          size="small"
          title="First page"
        >
          <ChevronsLeft />
        </IconButton>
        <IconButton
          aria-label="Previous page"
          disabled={onFirstPage}
          onClick={onPrev}
          size="small"
          title="Previous page"
        >
          <ChevronLeft />
        </IconButton>
        <IconButton
          aria-label="Next page"
          disabled={onLastPage}
          onClick={onNext}
          size="small"
          title="Next page"
        >
          <ChevronRight />
        </IconButton>
        <IconButton
          aria-label="Last page"
          disabled={onLastPage}
          onClick={onLast}
          size="small"
          title="Last page"
        >
          <ChevronsRight />
        </IconButton>
      </div>

      <div className="text-end text-sm text-content2">
        {total > 1 ? 'Records' : 'Record'} <strong>{clamp(1, total, skip)}</strong> -{' '}
        <strong>{clamp(1, total, skip + limit)}</strong> of <strong>{total}</strong>
      </div>
    </div>
  )
}
